import React, { useEffect, useState, useCallback } from "react";
import LivePl from "./LivePl/LivePl";
const fmt = require("indian-number-format");

export default function SummaryCard({ item = {}, handleSummaryCalculated, userType }) {
  const [pfdata, setPfdata] = useState([]);
  const [total, setTotal] = useState(0);

  // add "small" class if userType === 1
  const sizeClass = userType === 1 ? "small" : "";

  const LivePlTotal = useCallback((id, pf) => {
    if (!Array.isArray(item.standing) || item.standing.length === 0) return;

    setPfdata((prev) => {
      const updated = [...prev];
      const index = updated.findIndex((x) => x.id === id);

      if (index === -1) updated.push({ id, pf });
      else updated[index].pf = pf;

      const newTotal = updated.reduce((sum, entry) => sum + Number(entry.pf || 0), 0);
      setTotal(newTotal);

      return updated;
    });
  }, [item.standing]);

  useEffect(() => {
    if (typeof handleSummaryCalculated !== "function") return;

    const num = (val) => parseFloat(val || 0);
    const upperShare = num(item.upper_sharing);
    const selfShare = num(item.self_sharing);
    const lowerShare = num(item.lower_sharing);

    const liveUpper = (num(total) * upperShare) / 100;
    const liveSelf = (num(total) * selfShare) / 100;
    const liveLower = (num(total) * lowerShare) / 100;

    const combinedUpper = num(item.upper) - liveUpper;
    const combinedSelf = num(item.self) - liveSelf;
    const combinedLower = num(item.lower) - liveLower;

    handleSummaryCalculated({
      super_id: item.super_id,
      super_username: item.super_username,
      master_id: item.master_id,
      master_username: item.master_username,
      user_id: item.user_id,
      username: item.username,
      booked: num(item.booked).toFixed(0),
      deposit_total: num(item.deposit_total).toFixed(0),
      deposit_current: num(item.deposit_current).toFixed(0),
      total: num(total).toFixed(0),
      upper: num(combinedUpper).toFixed(0),
      self: num(combinedSelf).toFixed(0),
      lower: num(combinedLower).toFixed(0),
      upperLive: num(liveUpper).toFixed(0) * -1,
      selfLive: num(liveSelf).toFixed(0) * -1,
      lowerLive: num(liveLower).toFixed(0) * -1,
      shares: {
        upper: upperShare,
        self: selfShare,
        lower: lowerShare,
      },
    });
  }, [total, item, handleSummaryCalculated]);

  const openModal = (type, id) => {
    window.user_id = id;
    const modalFn =
      type === "super"
        ? window.openeditSuper
        : type === "master"
        ? window.masterModalOpen
        : window.userModalOpen;
    if (typeof modalFn === "function") modalFn();
  };

  const formatVal = (val) => fmt.format(parseFloat(Math.abs(val) || 0).toFixed(0));
  const isProfit = total > 0;

  const getCombined = (booked, shareKey) => {
    const shareValue = parseFloat(item[shareKey]) || 0;
    const livePortion = ((Math.abs(total) * shareValue) / 100) * (total >= 0 ? 1 : -1);
    const combined = parseFloat(booked || 0) - livePortion;

    return {
      value: combined,
      share: shareValue,
      color: combined >= 0 ? "buytext" : "selltext",
    };
  };

  return (
    <div className={`menuContainer addHight text ${sizeClass}`}>
      {/* Live P/L Rows */}
      {Array.isArray(item.standing) &&
        item.standing.map((i) => (
          <LivePl
            key={i.id}
            item={i}
            total={item.standing.length}
            LivePlTotal={LivePlTotal}
            className={sizeClass}
          />
        ))}

      {/* Username Columns */}
      {userType === 1 && (
        <div onClick={() => openModal("super", item.super_id)} className={`menuUsername large ${sizeClass}`}>
          <div className="user_id">{item.super_username}</div>
        </div>
      )}

      {(userType === 1 || userType === 2) && (
        <div onClick={() => openModal("master", item.master_id)} className={`menuUsername large ${sizeClass}`}>
          <div className="user_id">{item.master_username}</div>
        </div>
      )}

      <div onClick={() => openModal("user", item.user_id)} className={`menuUsername ${sizeClass}`}>
        <div className="user_id">{item.username}</div>
      </div>

      {/* Booked */}
      <div className={`menuUsername large ${item.booked > 0 ? "buytext" : "selltext"} ${sizeClass}`}>
        {formatVal(item.booked)}
      </div>

      {/* Deposit */}
      <div className={`menuUsername large ${sizeClass}`}>{formatVal(item.deposit_total)}</div>
      <div className={`menuUsername large ${sizeClass}`}>{formatVal(item.deposit_current)}</div>

      {/* Combined Upper / Self / Lower */}
      {[
        { key: "upper", shareKey: "upper_sharing" },
        { key: "self", shareKey: "self_sharing" },
        { key: "lower", shareKey: "lower_sharing" },
      ].map(({ key, shareKey }) => {
        const { value, share, color } = getCombined(item[key], shareKey);
        return (
          <div key={key} className={`menuName large ${color} ${sizeClass}`}>
            {formatVal(value)} ({share}%)
          </div>
        );
      })}

      {/* Live Breakdown */}
      {["upper_sharing", "self_sharing", "lower_sharing"].map((shareKey) => {
        const shareValue = parseFloat(item[shareKey]) || 0;
        const livePart = (total * shareValue) / 100;

        return (
          <div key={shareKey} className={`menuName large ${isProfit ? "selltext" : "buytext"} ${sizeClass}`}>
            {formatVal(Math.abs(livePart))} ({shareValue}%)
          </div>
        );
      })}

      {/* Net Total */}
      <div className={`menuName large ${isProfit ? "buytext" : "selltext"} ${sizeClass}`}>
        {formatVal(total)}
      </div>
    </div>
  );
}
